import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Alert,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  LocalOffer,
} from '@mui/icons-material';
import userService from '../../services/userService';

const DiscountRangeCarousel = ({ 
  minDiscount = 0, 
  maxDiscount = 100, 
  title, 
  category = null,
  limit = 20 
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [containerRef, setContainerRef] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const loadDiscountProducts = async () => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          page: 1,
          ordering: '-discount_percent' // Sort by discount descending
        };
        
        if (category) {
          params.category = category;
        }
        
        // Create a custom axios request with abort signal
        const response = await userService.getProductsByDiscountRange(
          minDiscount, 
          maxDiscount, 
          params,
          { signal: abortController.signal } // Pass abort signal
        );
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        // Limit to specified number of products
        const limitedProducts = (response.data.results || []).slice(0, limit);
        
        // Transform products to match expected format
        const transformedProducts = limitedProducts.map(item => {
          const images = item.images || [];
          const firstImage = Array.isArray(images) && images.length > 0 
            ? images[0] 
            : '/api/placeholder/200/200';
          
          return {
            id: item.id,
            name: item.name,
            manufacturer: item.manufacturer || 'N/A',
            price: item.price,
            best_price: item.best_price,
            discount_percent: item.discount_percent,
            image: firstImage,
            prescription_required: item.prescription_required,
            category: item.category,
            type: 'otc'
          };
        });
        
        setProducts(transformedProducts);
      } catch (err) {
        // Don't set error if request was aborted
        if (err.name === 'AbortError' || err.message === 'canceled' || err.code === 'ECONNABORTED') {
          console.log('Request was canceled');
          return;
        }
        console.error('Error loading discount products:', err);
        setError('Failed to load discount products');
        setProducts([]);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadDiscountProducts();
    
    // Cleanup: cancel request when component unmounts or dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [minDiscount, maxDiscount, category, limit]);

  const scrollLeft = () => {
    if (containerRef) {
      const cardWidth = 240;
      const newPosition = Math.max(0, scrollPosition - cardWidth);
      setScrollPosition(newPosition);
      containerRef.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (containerRef) {
      const cardWidth = 240;
      const maxScroll = containerRef.scrollWidth - containerRef.clientWidth;
      const newPosition = Math.min(maxScroll, scrollPosition + cardWidth);
      setScrollPosition(newPosition);
      containerRef.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  const handleCardClick = (product) => {
    if (product.type === 'otc' || product.category) {
      window.location.href = `/otc/${product.id}`;
    } else {
      window.location.href = `/product/${product.id}`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalOffer color="error" />
            <Skeleton variant="text" width={200} height={40} sx={{ ml: 1 }} />
            <Skeleton variant="rectangular" width={60} height={24} sx={{ ml: 2, borderRadius: 1 }} />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'primary.main',
              borderRadius: 4,
            },
          }}
        >
          {[...Array(5)].map((_, index) => (
            <Box
              key={index}
              sx={{
                minWidth: { xs: '200px', sm: '220px', md: '240px' },
                flexShrink: 0,
              }}
            >
              <Card sx={{ width: '240px', backgroundColor: 'white' }}>
                <Skeleton variant="rectangular" height={140} sx={{ p: 0.75 }} />
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Skeleton variant="text" width="90%" height={24} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.75 }} />
                  <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" width="100%" height={32} sx={{ borderRadius: 1, mt: 1 }} />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (products.length === 0) {
    return null; // Don't render if no products
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          backgroundColor: 'primary.main',
          px: { xs: 1, sm: 1.5, md: 2 },
          py: { xs: 1, sm: 1.25, md: 1.5 },
          borderRadius: 1,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <LocalOffer sx={{ color: 'white', mr: { xs: 0.5, sm: 0.75, md: 1 }, fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }} />
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              color: 'white',
              fontFamily: 'Lexend',
              fontWeight: 600,
              textTransform: 'uppercase',
              textAlign: 'left',
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem', lg: '1.1rem' },
            }}
          >
            {title}
          </Typography>
        </Box>
        {products.length > 0 && (
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton
              onClick={scrollLeft}
              disabled={scrollPosition <= 0}
              sx={{
                border: '1px solid',
                borderColor: 'white',
                borderRadius: '50%',
                color: 'white',
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
                '& svg': {
                  fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem', lg: '1.5rem' },
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={scrollRight}
              sx={{
                border: '1px solid',
                borderColor: 'white',
                borderRadius: '50%',
                color: 'white',
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& svg': {
                  fontSize: { xs: '0.75rem', sm: '1rem', md: '1.25rem', lg: '1.5rem' },
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>
      
      <Box
        ref={setContainerRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'primary.main',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          },
          '@media (max-width: 600px)': {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          },
        }}
      >
        {products.map((product) => (
          <Box
            key={product.id}
            sx={{
              minWidth: { xs: '200px', sm: '220px', md: '240px' },
              flexShrink: 0,
            }}
          >
            <Card 
              onClick={() => handleCardClick(product)}
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                cursor: 'pointer',
                width: '240px',
                backgroundColor: 'white',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="140"
                image={product.image || '/api/placeholder/200/200'}
                
                sx={{ objectFit: 'contain', p: 0.75 }}
              />
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography 
                  gutterBottom 
                  variant="subtitle1" 
                  component="div" 
                  sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.2,
                    minHeight: '2.4em',
                    fontSize: '0.9rem',
                    mb: 0.5,
                    fontWeight: 'bold',
                  }}
                >
                  {product.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 0.75,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.75rem',
                  }}
                >
                  {product.manufacturer}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    ₹{product.best_price || product.price}
                  </Typography>
                  {product.discount_percent && (
                    <Chip
                      label={`${Math.round(product.discount_percent * 100)}% OFF`}
                      size="small"
                      color="error"
                      sx={{ fontSize: '0.65rem', height: '20px' }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  size="small"
                  sx={{ mt: 'auto', fontSize: '0.75rem', py: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(product);
                  }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DiscountRangeCarousel;

